"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, House as Home, ShieldCheck, ArrowRight, HardHat, CircleCheck as CheckCircle2, Umbrella, Hammer, Flame, Layers, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const sectors = [{
  title: "Town centre commercial",
  description: "Pedestrian gantries, hoarding, and out-of-hours erection for busy retail and high street locations."
}, {
  title: "Office developments",
  description: "Independent tied access, facade scaffolding, and internal birdcages across Bracknell business parks."
}, {
  title: "Residential communities",
  description: "Careful access setups for private homes, housing estates, and residential refurbishments."
}, {
  title: "Mixed-use buildings",
  description: "Multi-level scaffolding engineered to keep retail units and public walkways fully operational."
}];
const residentialWork = ["Roof repairs & tile replacement", "Single & multi-storey extensions", "Chimney stack repairs & repointing", "External wall rendering & insulation", "Fascias, soffits, and gutter maintenance"];
const commercialWork = ["Main contractors & regional developers", "Commercial refurbishment & fit-outs", "Occupied premises & live site safety", "NASC & CHAS compliance", "Full weekly TG20:21 inspections"];
const tradeAccessLinks = [{
  title: "Roofing scaffolding",
  description: "Stable working platforms and safe perimeter edge protection for roofing teams.",
  to: "/RoofingScaffoldingPage",
  icon: Hammer
}, {
  title: "Temporary roofs",
  description: "Weatherproof tin hat coverings safeguarding open structures during major refurbishments.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge protection",
  description: "Handrails, toe boards, and debris netting ensuring complete fall protection compliance.",
  to: "/EdgeProtectionPage",
  icon: ShieldCheck
}];
const districtCoverage = [{
  code: "RG12",
  role: "Principal Bracknell district",
  description: "Core Bracknell coverage across the town centre, The Lexicon retail core, Crown Wood, Hanworth, Birch Hill, Easthampstead, and southern business parks."
}, {
  code: "RG42",
  role: "Northern Bracknell & adjoining coverage",
  description: "Northern Bracknell and adjoining area coverage, including Warfield, Newell Green, Winkfield Row, and the Binfield border."
}];
const nearbyAreas = [{
  name: "Wokingham",
  distance: "Approx. 5 miles",
  to: "/scaffolding-in-wokingham"
}, {
  name: "Ascot",
  distance: "Approx. 6 miles",
  to: "/scaffolding-in-ascot"
}, {
  name: "Reading",
  distance: "Approx. 11 miles",
  to: "/scaffolding-in-reading"
}];
export default function BracknellCoverage() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="bracknellservicescoverage" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-foreground">
            Service coverage
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Scaffolding services across Bracknell
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Safe, compliant access structures designed for town-centre commercial properties, office developments, residential streets, and roof refurbishment projects.
          </p>
        </motion.div>

        {/* Overview Sector Badges */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {sectors.map((sector, i) => <div key={sector.title} data-index={i} className="bg-card border border-border rounded-xl p-5 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
              <h3 className="font-default font-semibold text-base text-foreground mb-1">
                {sector.title}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {sector.description}
              </p>
            </div>)}
        </motion.div>

        {/* Primary Subsections: Residential vs Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Subsection 1: Residential */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp}>
            <Card className="h-full border-border bg-card shadow-sm flex flex-col justify-between">
              <CardHeader className="pb-4">
                <div className="size-11 rounded-lg bg-muted flex items-center justify-center text-primary mb-3">
                  <Home className="size-6" />
                </div>
                <CardTitle className="text-2xl font-default font-bold text-foreground">
                  Residential scaffolding in Bracknell
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm pt-1">
                  Reliable, tidy scaffolding erected with utmost care for domestic properties, driveways, and gardens.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 flex-1">
                <ul className="space-y-2.5">
                  {residentialWork.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>)}
                </ul>

                <Separator className="bg-border" />

                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild variant="outline" size="sm" className="font-medium">
                    <Link to="/ResidentialScaffoldingPage">
                      Residential scaffolding
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" size="sm" className="font-medium text-foreground">
                    <Link to="/ChimneyScaffoldingPage">
                      <Flame className="size-3.5 mr-1 text-primary" />
                      Chimney scaffolding
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Subsection 2: Commercial */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp}>
            <Card className="h-full border-border bg-card shadow-sm flex flex-col justify-between">
              <CardHeader className="pb-4">
                <div className="size-11 rounded-lg bg-muted flex items-center justify-center text-primary mb-3">
                  <Building2 className="size-6" />
                </div>
                <CardTitle className="text-2xl font-default font-bold text-foreground">
                  Commercial scaffolding in Bracknell
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm pt-1">
                  Engineered access systems designed for contractor schedules, occupied premises, and complex building facades.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 flex-1">
                <ul className="space-y-2.5">
                  {commercialWork.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <HardHat className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>)}
                </ul>

                <Separator className="bg-border" />

                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild variant="outline" size="sm" className="font-medium">
                    <Link to="/CommercialScaffoldingPage">
                      Commercial scaffolding
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" size="sm" className="font-medium text-foreground">
                    <Link to="/MainContractorsDevelopersPage">
                      <Layers className="size-3.5 mr-1 text-primary" />
                      Main contractors & developers
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="rounded-2xl bg-muted/60 border border-border p-6 md:p-8 mb-16">
          <div className="max-w-2xl mb-6">
            <h3 className="text-xl md:text-2xl font-default font-bold text-foreground mb-2">
              Roofing & trade access
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Specialist access, weather protection, and fall prevention erected for roofers, solar contractors, and building trades across Bracknell.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {tradeAccessLinks.map((item, i) => {
            const IconComp = item.icon;
            return <div key={item.title} data-index={i} className="bg-card border border-border rounded-xl p-5 flex flex-col justify-between shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                  <div>
                    <div className="size-9 rounded-md bg-muted flex items-center justify-center text-primary mb-3">
                      <IconComp className="size-5" />
                    </div>
                    <h4 className="font-default font-semibold text-base text-foreground mb-1.5">
                      {item.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      {item.description}
                    </p>
                  </div>
                  <Button asChild variant="link" className="p-0 h-auto self-start font-medium text-primary text-xs">
                    <Link to={item.to}>
                      Explore service
                      <ArrowRight className="size-3 ml-1" />
                    </Link>
                  </Button>
                </div>;
          })}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-border/80">
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
              Need a safety-led access quote for an upcoming project in Bracknell?
            </p>
            <Button asChild variant="default" size="sm" className="font-medium shrink-0">
              <Link to="/ContactRequestQuotePage">
                Request a Bracknell quote
                <ArrowRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Bracknell District Breakdown & Surrounding Areas */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Districts Column */}
            <div className="lg:col-span-7 space-y-5">
              <div>
                <Badge variant="outline" className="mb-2 border-primary/30 text-foreground">
                  Postal coverage
                </Badge>
                <h3 className="text-xl md:text-2xl font-default font-bold text-foreground">
                  Bracknell postal districts
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Comprehensive coverage across core Bracknell and adjoining northern Berkshire areas:
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {districtCoverage.map((item, i) => <div key={item.code} data-index={i} className={`rounded-xl border p-4.5 ${item.code === "RG12" ? "border-primary/40 bg-primary/5" : "border-border bg-muted/40"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-default font-bold text-base text-foreground bg-background px-2.5 py-0.5 rounded-md border border-border">
                        {item.code}
                      </span>
                      <span className="text-xs font-semibold text-primary">
                        {item.role}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* Nearby Areas Column */}
            <div className="lg:col-span-5 bg-muted/50 rounded-xl border border-border p-5 flex flex-col justify-between">
              <div>
                <h4 className="font-default font-semibold text-base text-foreground mb-1">
                  Nearby coverage areas
                </h4>
                <p className="text-xs text-muted-foreground mb-4">
                  Serving commercial contractors, developers, and homeowners across neighboring towns:
                </p>

                <div className="space-y-2 mb-4">
                  {nearbyAreas.map((area, i) => <Link key={area.name} to={area.to} data-index={i} className="flex items-center justify-between p-3 rounded-lg bg-card border border-border text-sm font-medium text-foreground transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-xs group">
                      <div className="flex items-center gap-2">
                        <MapPin className="size-4 text-primary shrink-0" />
                        <span className="group-hover:text-primary transition-colors">
                          {area.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{area.distance}</span>
                        <ArrowRight className="size-3 text-primary transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </Link>)}
                </div>
              </div>

              <div className="pt-3 border-t border-border/80 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Wider South East coverage
                </span>
                <Button asChild variant="link" className="p-0 h-auto font-medium text-primary text-xs">
                  <Link to="#">
                    View all areas
                    <ArrowRight className="size-3 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}